/**
 * Delivery Note Form Page
 * Epic 63: Uj szallitolevol letrehozasa
 */

import type { DeliveryNoteType } from '@/api/delivery-notes';
import type { Partner } from '@/api/partners';
import { PartnerSearchSelect } from '@/components/partners';
import { useCreateDeliveryNote } from '@/hooks/useDeliveryNotes';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FormItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DeliveryNoteFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createMutation = useCreateDeliveryNote();

  const typeOptions: Array<{ value: DeliveryNoteType; labelKey: string }> = [
    { value: 'STANDARD', labelKey: 'invoice.deliveryNote.types.standard' },
    { value: 'CONTRACT', labelKey: 'invoice.deliveryNote.types.contract' },
    { value: 'RENTAL_ACCESSORY', labelKey: 'invoice.deliveryNote.types.rentalAccessory' },
    { value: 'CREDIT', labelKey: 'invoice.deliveryNote.types.supplierCredit' },
  ];

  const [type, setType] = useState<DeliveryNoteType>('STANDARD');
  const [partnerName, setPartnerName] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<FormItem[]>([
    { productId: '', productName: '', quantity: 1, unitPrice: 0 },
  ]);

  const addItem = () => {
    setItems(prev => [...prev, { productId: '', productName: '', quantity: 1, unitPrice: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof FormItem, value: string | number) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const totalAmount = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const input: Parameters<typeof createMutation.mutateAsync>[0] = {
      type,
      partnerName,
      items: items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
    };

    if (partnerId) input.partnerId = partnerId;
    if (notes) input.notes = notes;

    await createMutation.mutateAsync(input);
    navigate('/delivery-notes');
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('invoice.deliveryNote.newDeliveryNote')}</h1>
        <button
          onClick={() => navigate('/delivery-notes')}
          className="rounded-lg border px-4 py-2 hover:bg-gray-50 dark:border-[#2d7d8a] dark:hover:bg-[#0f3d47]"
        >
          {t('invoice.deliveryNote.cancel')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Type selector */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('invoice.deliveryNote.typeFilterLabel')}
          </label>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {typeOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setType(option.value)}
                className={`rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors ${
                  type === option.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-gray-300 dark:border-[#2d7d8a] dark:hover:border-[#2d7d8a]'
                }`}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Partner fields */}
        <div className="space-y-3">
          <label className="block text-sm font-medium">
            {t('invoice.deliveryNote.partnerNameRequired')}
          </label>
          <PartnerSearchSelect
            variant="list"
            value={selectedPartner}
            onSelect={partner => {
              setSelectedPartner(partner);
              setPartnerName(partner.name);
              setPartnerId(partner.id);
            }}
          />
          {selectedPartner && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {selectedPartner.name}
              </p>
              {selectedPartner.taxNumber && (
                <p className="text-xs text-muted-foreground">
                  {t('invoice.taxNumber')}: {selectedPartner.taxNumber}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Items */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium">{t('invoice.deliveryNote.items')}</label>
            <button
              type="button"
              onClick={addItem}
              className="rounded-lg bg-gray-100 px-3 py-1 text-sm hover:bg-gray-200 dark:bg-[#1f5f6b] dark:hover:bg-[#0f3d47]"
            >
              {t('invoice.deliveryNote.addItem')}
            </button>
          </div>

          <div className="space-y-3">
            {items.map((item, index) => (
              <div key={index} className="rounded-lg border p-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
                  <div>
                    <label className="mb-1 block text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('invoice.deliveryNote.itemId')}
                    </label>
                    <input
                      type="text"
                      value={item.productId}
                      onChange={e => updateItem(index, 'productId', e.target.value)}
                      required
                      className="w-full rounded border px-2 py-1 text-sm"
                      placeholder={t('invoice.deliveryNote.itemIdPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('invoice.deliveryNote.description')}
                    </label>
                    <input
                      type="text"
                      value={item.productName}
                      onChange={e => updateItem(index, 'productName', e.target.value)}
                      required
                      className="w-full rounded border px-2 py-1 text-sm"
                      placeholder={t('invoice.deliveryNote.productNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('invoice.deliveryNote.quantity')}
                    </label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={e =>
                        updateItem(index, 'quantity', parseInt(e.target.value, 10) || 0)
                      }
                      min={1}
                      required
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('invoice.deliveryNote.unitPriceHuf')}
                    </label>
                    <input
                      type="number"
                      value={item.unitPrice}
                      onChange={e =>
                        updateItem(index, 'unitPrice', parseInt(e.target.value, 10) || 0)
                      }
                      min={0}
                      required
                      className="w-full rounded border px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs text-gray-500 dark:text-[#ccfbf1]">
                        {t('invoice.deliveryNote.amount')}
                      </label>
                      <div className="rounded bg-gray-50 px-2 py-1 text-sm font-medium dark:bg-[#1f5f6b]">
                        {formatCurrency(item.quantity * item.unitPrice)}
                      </div>
                    </div>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="rounded bg-red-50 px-2 py-1 text-sm text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300"
                      >
                        {t('invoice.deliveryNote.deleteItem')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 text-right text-lg font-bold">
            {t('invoice.deliveryNote.totalWithAmount', { amount: formatCurrency(totalAmount) })}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="mb-1 block text-sm font-medium">
            {t('invoice.deliveryNote.notesOptional')}
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border px-3 py-2"
            placeholder={t('invoice.deliveryNote.notesPlaceholder')}
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/delivery-notes')}
            className="rounded-lg border px-6 py-2 hover:bg-gray-50 dark:border-[#2d7d8a] dark:hover:bg-[#0f3d47]"
          >
            {t('invoice.deliveryNote.cancel')}
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !partnerName || items.length === 0}
            className="rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {createMutation.isPending
              ? t('invoice.deliveryNote.saving')
              : t('invoice.deliveryNote.createButton')}
          </button>
        </div>

        {createMutation.isError && (
          <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
            {t('invoice.deliveryNote.errorPrefix', {
              message: createMutation.error?.message ?? t('invoice.deliveryNote.unknownError'),
            })}
          </div>
        )}
      </form>
    </div>
  );
}
